"use client";

import { c as _c } from "react/compiler-runtime";
import type { Locale } from "@lib/i18n/locale";
import type { ArticleTeaser } from "@modules/article/lib/teasers";
import { SEARCH_PARAMS } from "@modules/hub/lib/hubPath";
import searchArticles from "@modules/hub/lib/searchArticles";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
export const MIN_QUERY_LENGTH = 2;
export type SearchOutcome = {
  query: string;
  teasers: ArticleTeaser[];
} | {
  query: string;
  failed: true;
};
interface HubSearchState {
  query: string;
  outcome: SearchOutcome | null;
  busy: boolean;
  submit: (query: string) => void;
}
const HubSearchContext = createContext<HubSearchState>({
  query: "",
  outcome: null,
  busy: false,
  submit: () => {}
});
export const useHubSearch = () => {
  return useContext(HubSearchContext);
};
const subscribeToHistory = (onChange: () => void) => {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
};
const writeQueryParam = (param: string, value: string) => {
  const url = new URL(window.location.href);
  if (value) {
    url.searchParams.set(param, value);
  } else {
    url.searchParams.delete(param);
  }
  window.history.replaceState(window.history.state, "", url);
};
export const HubSearchProvider = t0 => {
  const $ = _c(23);
  const {
    locale,
    category,
    children
  } = t0;
  const param = SEARCH_PARAMS[locale];
  let t1;
  if ($[0] !== param) {
    t1 = () => new URLSearchParams(window.location.search).get(param) ?? "";
    $[0] = param;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const restored = useSyncExternalStore(subscribeToHistory, t1, _temp);
  const [submitted, setSubmitted] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const t2 = submitted ?? restored;
  let t3;
  if ($[2] !== t2) {
    t3 = t2.trim();
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const trimmed = t3;
  const query = trimmed.length >= MIN_QUERY_LENGTH ? trimmed : "";
  let t4;
  if ($[4] !== category || $[5] !== locale || $[6] !== query) {
    t4 = () => {
      if (!query) {
        return;
      }
      const controller = new AbortController();
      const search = async () => {
        try {
          const teasers = await searchArticles({
            query,
            locale,
            category,
            signal: controller.signal
          });
          setOutcome({
            query,
            teasers
          });
        } catch {
          if (!controller.signal.aborted) {
            setOutcome({
              query,
              failed: true
            });
          }
        }
      };
      search();
      return () => controller.abort();
    };
    $[4] = category;
    $[5] = locale;
    $[6] = query;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== attempt || $[9] !== category || $[10] !== locale || $[11] !== query) {
    t5 = [query, locale, category, attempt];
    $[8] = attempt;
    $[9] = category;
    $[10] = locale;
    $[11] = query;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== param) {
    t6 = next => {
      const value = next.trim();
      setSubmitted(value);
      setAttempt(_temp2);
      writeQueryParam(param, value);
    };
    $[13] = param;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const submit = t6;
  const t7 = query ? outcome : null;
  const t8 = query !== "" && outcome?.query !== query;
  let t9;
  if ($[15] !== query || $[16] !== submit || $[17] !== t7 || $[18] !== t8) {
    t9 = {
      query,
      outcome: t7,
      busy: t8,
      submit
    };
    $[15] = query;
    $[16] = submit;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const state = t9;
  let t10;
  if ($[20] !== children || $[21] !== state) {
    t10 = <HubSearchContext value={state}>{children}</HubSearchContext>;
    $[20] = children;
    $[21] = state;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
function _temp() {
  return "";
}
function _temp2(count) {
  return count + 1;
}